import type { View } from '../router/router';
import { router } from '../router/router';
import { apiClient } from '../services/api-client';
import { toast } from '../components/layout/toast';
import { $ } from '../utils/dom';
import { escapeHtml } from '../utils/catalog-labels';
import {
  formatRoutineDate,
  routineStatusBadge,
  toDateInputValue,
} from '../utils/routine-labels';
import { getTyrisIcon } from '../components/ui/tyris-icon';

interface CatalogExercise {
  id: string;
  name: string;
  restSec: number;
  machine?: { name: string; code: string } | null;
}

interface RoutineExerciseLine {
  exerciseId: string;
  sets: number;
  reps: string;
  weight?: number | null;
  rpe?: number | null;
  rir?: number | null;
  restSec?: number | null;
  order: number;
  exercise?: CatalogExercise;
}

interface RoutineDetail {
  id: string;
  name: string;
  description?: string | null;
  startDate: string;
  endDate?: string | null;
  isActive: boolean;
  effectiveStatus?: import('../utils/routine-labels').RoutineEffectiveStatus;
  studentId: string;
  student?: { firstName: string; lastName: string; email: string };
  exercises: Array<
    RoutineExerciseLine & {
      id?: string;
      exercise: CatalogExercise & { machine?: { name: string; code: string } | null };
    }
  >;
}

type PageMode = 'view' | 'create' | 'edit';

export class AdminRoutinePage implements View {
  private mode: PageMode = 'view';
  private routineId = '';
  private studentId = '';
  private originFrom = '';     // 'student' | '' — de dónde venimos
  private originStudentId = ''; // studentId de la ficha de origen
  private routine: RoutineDetail | null = null;
  private catalog: CatalogExercise[] = [];
  private lines: RoutineExerciseLine[] = [];
  private loading = true;
  private errorMsg: string | null = null;

  async render(): Promise<string> {
    this.readParams();
    await this.loadData();

    if (this.loading) {
      return `<div class="admin-state-loading" data-testid="admin-routine-loading"><p>Cargando rutina...</p></div>`;
    }

    if (this.errorMsg) {
      return `
        <div class="card-glass admin-state-error" data-testid="admin-routine-error">
          <h3>Error</h3>
          <p>${escapeHtml(this.errorMsg)}</p>
          <a href="#/admin/routines" class="btn btn-outline">← Volver</a>
        </div>`;
    }

    if (this.mode === 'view' && this.routine) {
      return this.renderView();
    }

    if (this.mode === 'create' || this.mode === 'edit') {
      return this.renderForm();
    }

    return `<div class="admin-empty"><p>Rutina no encontrada.</p></div>`;
  }

  afterRender(): void {
    if (this.mode === 'view') {
      $('#btn-delete-routine')?.addEventListener('click', () => this.handleDelete());
      $('#btn-activate-routine')?.addEventListener('click', () => this.handleActivate());
      return;
    }

    $('#btn-cancel-routine')?.addEventListener('click', () => {
      if (this.mode === 'edit' && this.routineId) {
        window.location.hash = `#/admin/routine?id=${this.routineId}`;
      } else if (this.studentId) {
        window.location.hash = `#/admin/routines?studentId=${this.studentId}`;
      } else {
        window.location.hash = '#/admin/routines';
      }
    });

    $('#btn-add-exercise-line')?.addEventListener('click', () => this.addExerciseLine());
    $('#form-routine')?.addEventListener('submit', (e) => this.handleSubmit(e));

    document.querySelectorAll('[data-move-up]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).getAttribute('data-move-up'));
        this.moveLine(idx, -1);
      });
    });

    document.querySelectorAll('[data-move-down]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).getAttribute('data-move-down'));
        this.moveLine(idx, 1);
      });
    });

    document.querySelectorAll('[data-remove-line]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).getAttribute('data-remove-line'));
        this.removeLine(idx);
      });
    });
  }

  private renderView(): string {
    const r = this.routine!;
    const badge = routineStatusBadge(r);
    const sorted = [...r.exercises].sort((a, b) => a.order - b.order);
    const studentName = r.student
      ? `${r.student.firstName} ${r.student.lastName}`
      : 'Alumno';

    // Decidir a dónde lleva el botón "← Volver"
    const backHref = this.originFrom === 'student' && this.originStudentId
      ? `#/admin/student?id=${this.originStudentId}`
      : `#/admin/routines?studentId=${r.studentId}`;
    const backLabel = this.originFrom === 'student'
      ? `← Ficha de ${escapeHtml(studentName)}`
      : `← Rutinas de ${escapeHtml(studentName)}`;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <a href="${backHref}" class="admin-back-link">${backLabel}</a>
          <h2 data-testid="admin-routine-name">${escapeHtml(r.name)}</h2>
          <p>${escapeHtml(r.description ?? 'Sin descripción')}</p>
        </div>
        <span class="${badge.className}" data-testid="admin-routine-status-badge">${badge.label}</span>
      </div>

      <div class="admin-detail-grid">
        <section class="card-glass" data-testid="admin-routine-meta">
          <h3 class="admin-section-title">Datos generales</h3>
          <dl class="admin-dl">
            <dt>Alumno</dt><dd>${escapeHtml(studentName)}</dd>
            <dt>Inicio</dt><dd>${formatRoutineDate(r.startDate)}</dd>
            <dt>Fin</dt><dd>${formatRoutineDate(r.endDate)}</dd>
            <dt>Estado</dt><dd><span class="${badge.className}">${badge.label}</span></dd>
          </dl>
        </section>
        <section class="card-glass">
          <h3 class="admin-section-title">Acciones</h3>
          <div class="admin-quick-links">
            <a href="#/admin/routine?form=edit&id=${r.id}" class="btn btn-primary" data-testid="btn-edit-routine" style="display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('editar', { size: 16 })} Editar rutina</a>
            ${
              !r.isActive
                ? `<button type="button" class="btn btn-outline" id="btn-activate-routine" data-testid="btn-activate-routine">Marcar como activa</button>`
                : ''
            }
            <button type="button" class="btn btn-outline" id="btn-delete-routine" data-testid="btn-delete-routine"
              style="border-color:rgba(220,53,69,0.35);color:hsl(350,80%,65%);display:inline-flex;align-items:center;gap:0.4rem;">${getTyrisIcon('darse-de-baja', { size: 16 })} Eliminar</button>
          </div>
        </section>
      </div>

      <section class="card-glass admin-table-wrap" style="margin-top:1.5rem;" data-testid="admin-routine-exercises">
        <h3 class="admin-section-title">Ejercicios planificados (${sorted.length})</h3>
        <table class="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Ejercicio</th>
              <th>Series</th>
              <th>Reps</th>
              <th>Peso</th>
              <th>RPE</th>
              <th>RIR</th>
              <th>Descanso (s)</th>
              <th>Máquina</th>
            </tr>
          </thead>
          <tbody>
            ${sorted
              .map(
                (line, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td><strong>${escapeHtml(line.exercise?.name ?? '—')}</strong></td>
                <td>${line.sets}</td>
                <td>${escapeHtml(line.reps)}</td>
                <td>${line.weight != null ? `${line.weight} kg` : '—'}</td>
                <td>${line.rpe ?? '—'}</td>
                <td>${line.rir ?? '—'}</td>
                <td>${this.renderEffectiveRest(line)}</td>
                <td class="admin-cell-muted">${line.exercise?.machine ? `${escapeHtml(line.exercise.machine.name)} (${escapeHtml(line.exercise.machine.code)})` : '—'}</td>
              </tr>
            `,
              )
              .join('')}
          </tbody>
        </table>
      </section>
    `;
  }

  private renderForm(): string {
    const r = this.routine;
    const title = this.mode === 'create' ? 'Nueva rutina asignada' : 'Editar rutina';
    const name = r?.name ?? '';
    const description = r?.description ?? '';
    const startDate = toDateInputValue(r?.startDate) || new Date().toISOString().split('T')[0];
    const endDate = toDateInputValue(r?.endDate);
    const isActive = r?.isActive ?? true;

    return `
      <div class="dashboard-header">
        <div class="dashboard-title">
          <h2 data-testid="admin-routine-form-title">${title}</h2>
          <p>Constructor de rutina con ejercicios del catálogo.</p>
        </div>
      </div>

      <div class="admin-api-notice card-glass" data-testid="routine-builder-notice">
        <strong>Rutina del alumno:</strong> copia editable e independiente. Si proviene de una template, editar aquí no modifica la template base.
      </div>

      <form id="form-routine" class="card-glass admin-form-card" data-testid="admin-routine-form">
        <div class="admin-form-grid">
          <label>Nombre *
            <input name="name" class="admin-input" required value="${escapeHtml(name)}" data-testid="routine-form-name" />
          </label>
          <label>Inicio
            <input type="date" name="startDate" class="admin-input" value="${startDate}" data-testid="routine-form-start" />
          </label>
          <label>Fin (opcional)
            <input type="date" name="endDate" class="admin-input" value="${endDate}" data-testid="routine-form-end" />
          </label>
        </div>
        <label style="display:block;margin-top:1rem;">Descripción
          <textarea name="description" class="admin-input admin-textarea" rows="2" data-testid="routine-form-description">${escapeHtml(description)}</textarea>
        </label>
        <label class="admin-checkbox-row" style="margin-top:1rem;">
          <input type="checkbox" name="isActive" ${isActive ? 'checked' : ''} data-testid="routine-form-active" />
          Dejar como rutina activa del alumno (desactiva las demás activas)
        </label>

        <div style="margin-top:1.5rem;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.75rem;">
            <h3 class="admin-section-title" style="margin:0;">Ejercicios</h3>
            <button type="button" class="btn btn-outline btn-sm" id="btn-add-exercise-line" data-testid="btn-add-exercise-line" style="display:inline-flex;align-items:center;gap:0.25rem;">${getTyrisIcon('plus', { size: 14 })} Agregar ejercicio</button>
          </div>
          <div id="routine-lines-container" data-testid="routine-lines-container">
            ${this.lines.length === 0 ? `<p class="admin-cell-muted">Agrega al menos un ejercicio.</p>` : this.renderLinesEditor()}
          </div>
        </div>

        <div class="admin-form-actions">
          <button type="submit" class="btn btn-primary" data-testid="routine-form-submit">Guardar rutina</button>
          <button type="button" class="btn btn-outline" id="btn-cancel-routine">Cancelar</button>
        </div>
      </form>
    `;
  }

  private getExerciseDefaultRest(exerciseId: string): number {
    return this.catalog.find((ex) => ex.id === exerciseId)?.restSec ?? 120;
  }

  private renderEffectiveRest(line: RoutineExerciseLine): string {
    if (line.restSec != null) return `${line.restSec} s`;
    const fallback = line.exercise?.restSec ?? this.getExerciseDefaultRest(line.exerciseId);
    return `${fallback} s (auto)`;
  }

  private renderLinesEditor(): string {
    return `
      <table class="admin-table routine-builder-table">
        <thead>
          <tr>
            <th>Orden</th>
            <th>Ejercicio</th>
            <th>Series</th>
            <th>Reps</th>
            <th>Peso (kg)</th>
            <th>RPE</th>
            <th>RIR</th>
            <th>Descanso (s)</th>
            <th></th>
          </tr>
        </thead>
        <tbody id="routine-lines-body">
          ${this.lines.map((line, idx) => this.renderLineRow(line, idx)).join('')}
        </tbody>
      </table>
    `;
  }

  private renderLineRow(line: RoutineExerciseLine, idx: number): string {
    return `
      <tr data-line-index="${idx}">
        <td>
          <span class="routine-order-badge">${idx + 1}</span>
          <div class="admin-row-actions" style="margin-top:0.25rem;">
            <button type="button" class="btn btn-outline btn-sm" data-move-up="${idx}" ${idx === 0 ? 'disabled' : ''}>↑</button>
            <button type="button" class="btn btn-outline btn-sm" data-move-down="${idx}" ${idx === this.lines.length - 1 ? 'disabled' : ''}>↓</button>
          </div>
        </td>
        <td>
          <select class="admin-select line-exercise-id" data-idx="${idx}" required data-testid="routine-line-exercise-${idx}">
            <option value="">— Seleccionar —</option>
            ${this.catalog
              .map(
                (ex) => `
              <option value="${ex.id}" ${line.exerciseId === ex.id ? 'selected' : ''}>${escapeHtml(ex.name)}</option>
            `,
              )
              .join('')}
          </select>
        </td>
        <td><input type="number" class="admin-input line-sets" data-idx="${idx}" min="1" value="${line.sets}" required style="width:4rem;" data-testid="routine-line-sets-${idx}" /></td>
        <td>
          <input type="text" class="admin-input line-reps" data-idx="${idx}" value="${escapeHtml(line.reps)}" required
            style="width:6rem;" placeholder="ej: 8-12" data-testid="routine-line-reps-${idx}" />
          <div style="font-size:0.7rem;color:var(--text-muted);margin-top:0.2rem;">ej: 10, 8-12, fallo</div>
        </td>
        <td><input type="number" class="admin-input line-weight" data-idx="${idx}" min="0" step="0.5" value="${line.weight ?? ''}" style="width:5rem;" /></td>
        <td><input type="number" class="admin-input line-rpe" data-idx="${idx}" min="1" max="10" value="${line.rpe ?? ''}" style="width:3.5rem;" /></td>
        <td><input type="number" class="admin-input line-rir" data-idx="${idx}" min="0" max="10" value="${line.rir ?? ''}" style="width:3.5rem;" title="RIR: 0=fallo, 1-4=zona de trabajo, 5+=ligero" /></td>
        <td><input type="number" class="admin-input line-rest" data-idx="${idx}" min="0" max="600" step="5"
          value="${line.restSec ?? ''}" placeholder="Auto (${this.getExerciseDefaultRest(line.exerciseId)} s)"
          style="width:7rem;" title="Vacío = hereda el descanso del ejercicio"
          data-testid="routine-line-rest-${idx}" /></td>
        <td><button type="button" class="btn btn-outline btn-sm" data-remove-line="${idx}" style="display:inline-flex;align-items:center;gap:0.4rem;color:hsl(350,80%,65%);">${getTyrisIcon('close', { size: 16 })}</button></td>
      </tr>
    `;
  }

  private readParams(): void {
    const params = router.getParams();
    this.routineId = params.id ?? '';
    this.studentId = params.studentId ?? '';
    this.originFrom = params.from ?? '';
    this.originStudentId = params.studentId ?? '';

    if (params.form === 'create') {
      this.mode = 'create';
    } else if (params.form === 'edit' && params.id) {
      this.mode = 'edit';
      this.routineId = params.id;
    } else if (params.id) {
      this.mode = 'view';
    } else {
      this.mode = 'view';
    }
  }

  private async loadData(): Promise<void> {
    try {
      this.catalog = await apiClient.get<CatalogExercise[]>('/api/exercises');

      if (this.mode === 'view' || this.mode === 'edit') {
        if (!this.routineId) {
          this.errorMsg = 'ID de rutina no especificado';
          return;
        }
        this.routine = await apiClient.get<RoutineDetail>(`/api/routines/${this.routineId}`);
        this.studentId = this.routine.studentId;
        if (this.mode === 'edit') {
          this.lines = this.routine.exercises
            .map((ex) => ({
              exerciseId: ex.exerciseId ?? ex.exercise?.id ?? '',
              sets: ex.sets,
              reps: ex.reps,
              weight: ex.weight,
              rpe: ex.rpe,
              rir: ex.rir,
              restSec: ex.restSec,
              order: ex.order,
              exercise: ex.exercise,
            }))
            .sort((a, b) => a.order - b.order);
        }
      } else if (this.mode === 'create') {
        if (!this.studentId) {
          this.errorMsg = 'Selecciona un alumno para asignar la rutina';
          return;
        }
        this.lines =
          this.catalog.length > 0
            ? [
                {
                  exerciseId: this.catalog[0].id,
                  sets: 3,
                  reps: '8-12',
                  order: 1,
                },
              ]
            : [];
      }
      this.errorMsg = null;
    } catch (err: unknown) {
      this.errorMsg = err instanceof Error ? err.message : 'Error al cargar';
    } finally {
      this.loading = false;
    }
  }

  private syncLinesFromDom(): void {
    const rows = document.querySelectorAll('#routine-lines-body tr');
    rows.forEach((row, idx) => {
      const line = this.lines[idx];
      if (!line) return;
      const exerciseSelect = row.querySelector('.line-exercise-id') as HTMLSelectElement;
      const setsInput = row.querySelector('.line-sets') as HTMLInputElement;
      const repsInput = row.querySelector('.line-reps') as HTMLInputElement;
      const weightInput = row.querySelector('.line-weight') as HTMLInputElement;
      const rpeInput = row.querySelector('.line-rpe') as HTMLInputElement;
      const rirInput = row.querySelector('.line-rir') as HTMLInputElement;

      if (exerciseSelect) line.exerciseId = exerciseSelect.value;
      if (setsInput) line.sets = Number(setsInput.value) || 1;
      if (repsInput) line.reps = repsInput.value;
      if (weightInput && weightInput.value !== '') line.weight = Number(weightInput.value);
      else line.weight = null;
      if (rpeInput && rpeInput.value !== '') line.rpe = Number(rpeInput.value);
      else line.rpe = null;
      if (rirInput && rirInput.value !== '') line.rir = Number(rirInput.value);
      else line.rir = null;
      const restInput = row.querySelector('.line-rest') as HTMLInputElement;
      if (restInput && restInput.value !== '') line.restSec = Number(restInput.value);
      else line.restSec = null;
      line.order = idx + 1;
    });
  }

  private async refreshLinesDom(): Promise<void> {
    const container = $('#routine-lines-container');
    if (!container) return;
    container.innerHTML =
      this.lines.length === 0
        ? `<p class="admin-cell-muted">Agrega al menos un ejercicio.</p>`
        : this.renderLinesEditor();
    await this.afterRenderLines();
  }

  private async afterRenderLines(): Promise<void> {
    document.querySelectorAll('[data-move-up]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).getAttribute('data-move-up'));
        this.moveLine(idx, -1);
      });
    });
    document.querySelectorAll('[data-move-down]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).getAttribute('data-move-down'));
        this.moveLine(idx, 1);
      });
    });
    document.querySelectorAll('[data-remove-line]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = Number((btn as HTMLElement).getAttribute('data-remove-line'));
        this.removeLine(idx);
      });
    });
    document.querySelectorAll('.line-exercise-id').forEach((select) => {
      select.addEventListener('change', () => {
        const row = select.closest('tr');
        const restInput = row?.querySelector('.line-rest') as HTMLInputElement | null;
        if (restInput) {
          restInput.placeholder = `Auto (${this.getExerciseDefaultRest(
            (select as HTMLSelectElement).value,
          )} s)`;
        }
      });
    });
  }

  private addExerciseLine(): void {
    this.syncLinesFromDom();
    const defaultEx = this.catalog[0];
    if (!defaultEx) {
      toast.error('No hay ejercicios en el catálogo. Crea ejercicios primero.');
      return;
    }
    this.lines.push({
      exerciseId: defaultEx.id,
      sets: 3,
      reps: '8-12',
      order: this.lines.length + 1,
    });
    void this.refreshLinesDom();
  }

  private removeLine(idx: number): void {
    this.syncLinesFromDom();
    this.lines.splice(idx, 1);
    this.lines.forEach((l, i) => {
      l.order = i + 1;
    });
    void this.refreshLinesDom();
  }

  private moveLine(idx: number, delta: number): void {
    this.syncLinesFromDom();
    const target = idx + delta;
    if (target < 0 || target >= this.lines.length) return;
    const tmp = this.lines[idx];
    this.lines[idx] = this.lines[target];
    this.lines[target] = tmp;
    this.lines.forEach((l, i) => {
      l.order = i + 1;
    });
    void this.refreshLinesDom();
  }

  private collectPayload(form: HTMLFormElement) {
    this.syncLinesFromDom();
    const fd = new FormData(form);
    const name = String(fd.get('name') ?? '').trim();
    const description = String(fd.get('description') ?? '').trim();
    const startDate = String(fd.get('startDate') ?? '');
    const endDate = String(fd.get('endDate') ?? '');
    const isActive = fd.get('isActive') === 'on';

    if (!name) {
      toast.error('El nombre de la rutina es obligatorio.');
      return null;
    }

    if (this.lines.length === 0) {
      toast.error('Agrega al menos un ejercicio a la rutina.');
      return null;
    }

    for (const line of this.lines) {
      if (!line.exerciseId || !line.reps.trim()) {
        toast.error('Completa ejercicio y repeticiones en cada línea.');
        return null;
      }
    }

    const exercises = this.lines.map((l, i) => ({
      exerciseId: l.exerciseId,
      sets: l.sets,
      reps: l.reps.trim(),
      weight: l.weight ?? undefined,
      rpe: l.rpe ?? undefined,
      rir: l.rir ?? undefined,
      ...(l.restSec != null ? { restSec: l.restSec } : {}),
      order: i + 1,
    }));

    return {
      name,
      description: description || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      isActive,
      studentId: this.studentId,
      exercises,
    };
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const payload = this.collectPayload(form);
    if (!payload) return;

    if (this.mode === 'create' && !this.studentId) {
      toast.error('Alumno no especificado.');
      return;
    }

    const activateMsg =
      payload.isActive
        ? 'Al marcar como activa, las demás rutinas activas del alumno pasarán a históricas. ¿Continuar?'
        : null;
    if (activateMsg && !confirm(activateMsg)) return;

    try {
      if (this.mode === 'create') {
        const created = await apiClient.post<{ id: string }>('/api/routines', payload);
        toast.success('Rutina asignada correctamente.');
        window.location.hash = `#/admin/routine?id=${created.id}`;
      } else if (this.mode === 'edit' && this.routineId) {
        const { studentId: _s, ...updatePayload } = payload;
        await apiClient.patch(`/api/routines/${this.routineId}`, updatePayload);
        toast.success('Rutina actualizada.');
        window.location.hash = `#/admin/routine?id=${this.routineId}`;
      }
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string | string[] } })?.body;
      const msg = Array.isArray(body?.message) ? body.message.join(', ') : body?.message;
      toast.error(msg ?? 'No se pudo guardar la rutina.');
    }
  }

  private async handleDelete(): Promise<void> {
    if (!this.routineId || !this.routine) return;
    if (
      !confirm(
        `¿Eliminar la rutina "${this.routine.name}"? Esta acción es irreversible y el alumno dejará de verla.`,
      )
    ) {
      return;
    }
    try {
      await apiClient.delete(`/api/routines/${this.routineId}`);
      toast.success('Rutina eliminada.');
      window.location.hash = `#/admin/routines?studentId=${this.routine.studentId}`;
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } })?.body;
      toast.error(body?.message ?? 'No se pudo eliminar.');
    }
  }

  private async handleActivate(): Promise<void> {
    if (!this.routineId) return;
    if (!confirm('¿Marcar esta rutina como activa? Las demás rutinas activas del alumno quedarán inactivas.')) {
      return;
    }
    try {
      await apiClient.patch(`/api/routines/${this.routineId}`, { isActive: true });
      toast.success('Rutina activada.');
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } catch (err: unknown) {
      const body = (err as { body?: { message?: string } })?.body;
      toast.error(body?.message ?? 'No se pudo activar.');
    }
  }
}
